import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Building,
  FileText,
  AlertTriangle,
  Plus,
  Trash2,
  BarChart3,
  PieChart,
  Calendar,
  Calculator,
  Settings,
  RefreshCw,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Analytics = () => {
  const { isOfficeManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [profitMargin, setProfitMargin] = useState(null);
  const [employeeHours, setEmployeeHours] = useState(null);
  const [discrepancies, setDiscrepancies] = useState(null);
  const [seasonalForecast, setSeasonalForecast] = useState(null);
  const [profitSettings, setProfitSettings] = useState(null);
  const [expenditures, setExpenditures] = useState([]);
  
  // Dialog states
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [expenditureDialogOpen, setExpenditureDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Date range for profit margin
  const [startDate, setStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  
  // Forms
  const [newOverhead, setNewOverhead] = useState({ name: "", amount: 0, category_type: "fixed" });
  const [newManualAddition, setNewManualAddition] = useState({ name: "", amount: 0 });
  const [newExpenditure, setNewExpenditure] = useState({
    name: "", category: "other", amount: 0, date: new Date().toISOString().split("T")[0], description: ""
  });
  const [newSeasonalPeriod, setNewSeasonalPeriod] = useState({
    name: "", start_month: 1, start_day: 1, end_month: 12, end_day: 31
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard", error);
    }
  }, []);

  const fetchYearlyStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/yearly-stats?year=${selectedYear}`);
      setYearlyStats(response.data);
    } catch (error) {
      console.error("Failed to fetch yearly stats", error);
    }
  }, [selectedYear]);

  const fetchProfitMargin = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/profit-margin?start_date=${startDate}&end_date=${endDate}&calculation_type=all`
      );
      setProfitMargin(response.data);
    } catch (error) {
      console.error("Failed to fetch profit margin", error);
    }
  }, [startDate, endDate]);

  const fetchEmployeeHours = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/employee-hours?start_date=${startDate}&end_date=${endDate}`
      );
      setEmployeeHours(response.data);
    } catch (error) {
      console.error("Failed to fetch employee hours", error);
    }
  }, [startDate, endDate]);

  const fetchDiscrepancies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/discrepancies?year=${selectedYear}`);
      setDiscrepancies(response.data);
    } catch (error) {
      console.error("Failed to fetch discrepancies", error);
    }
  }, [selectedYear]);

  const fetchSeasonalForecast = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/seasonal-forecast`);
      setSeasonalForecast(response.data);
    } catch (error) {
      console.error("Failed to fetch seasonal forecast", error);
    }
  }, []);

  const fetchProfitSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/profit-margin/settings`);
      setProfitSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings", error);
    }
  }, []);

  const fetchExpenditures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/expenditures`);
      setExpenditures(response.data);
    } catch (error) {
      console.error("Failed to fetch expenditures", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchYearlyStats(),
        fetchProfitMargin(),
        fetchEmployeeHours(),
        fetchDiscrepancies(),
        fetchSeasonalForecast(),
        fetchProfitSettings(),
        fetchExpenditures(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchYearlyStats, fetchProfitMargin, fetchEmployeeHours, fetchDiscrepancies, fetchSeasonalForecast, fetchProfitSettings, fetchExpenditures]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/analytics/profit-margin/settings`, profitSettings);
      toast.success("Settings saved");
      setSettingsDialogOpen(false);
      fetchProfitMargin();
      fetchSeasonalForecast();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddOverhead = () => {
    if (!newOverhead.name) return;
    setProfitSettings({
      ...profitSettings,
      overhead_categories: [...(profitSettings?.overhead_categories || []), newOverhead]
    });
    setNewOverhead({ name: "", amount: 0, category_type: "fixed" });
  };

  const handleRemoveOverhead = (index) => {
    const updated = [...(profitSettings?.overhead_categories || [])];
    updated.splice(index, 1);
    setProfitSettings({ ...profitSettings, overhead_categories: updated });
  };

  const handleAddManualAddition = () => {
    if (!newManualAddition.name) return;
    setProfitSettings({
      ...profitSettings,
      manual_additions: [...(profitSettings?.manual_additions || []), newManualAddition]
    });
    setNewManualAddition({ name: "", amount: 0 });
  };

  const handleRemoveManualAddition = (index) => {
    const updated = [...(profitSettings?.manual_additions || [])];
    updated.splice(index, 1);
    setProfitSettings({ ...profitSettings, manual_additions: updated });
  };

  const handleAddSeasonalPeriod = () => {
    if (!newSeasonalPeriod.name) return;
    setProfitSettings({
      ...profitSettings,
      seasonal_periods: [...(profitSettings?.seasonal_periods || []), newSeasonalPeriod]
    });
    setNewSeasonalPeriod({ name: "", start_month: 1, start_day: 1, end_month: 12, end_day: 31 });
  };

  const handleRemoveSeasonalPeriod = (index) => {
    const updated = [...(profitSettings?.seasonal_periods || [])];
    updated.splice(index, 1);
    setProfitSettings({ ...profitSettings, seasonal_periods: updated });
  };

  const handleCreateExpenditure = async () => {
    if (!newExpenditure.name || !newExpenditure.amount) {
      toast.error("Name and amount required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/analytics/expenditures`, newExpenditure);
      toast.success("Expenditure added");
      setExpenditureDialogOpen(false);
      setNewExpenditure({ name: "", category: "other", amount: 0, date: new Date().toISOString().split("T")[0], description: "" });
      fetchExpenditures();
      fetchProfitMargin();
    } catch (error) {
      toast.error("Failed to add expenditure");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpenditure = async (id) => {
    if (!window.confirm("Delete this expenditure?")) return;
    try {
      await axios.delete(`${API}/analytics/expenditures/${id}`);
      toast.success("Expenditure deleted");
      fetchExpenditures();
      fetchProfitMargin();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const refreshData = () => {
    fetchProfitMargin();
    fetchEmployeeHours();
    fetchYearlyStats();
    fetchDiscrepancies();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

  return (
    <div className="space-y-6 animate-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-primary" />
            Business Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive business reports and profit analysis
          </p>
        </div>
        <div className="flex gap-2">
          {isOfficeManager && (
            <Button variant="outline" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          )}
          <Button variant="outline" onClick={refreshData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span className="text-sm">Yearly Quotes</span>
              </div>
              <p className="text-2xl font-bold">{dashboard.yearly_summary?.total_quotes || 0}</p>
              <p className="text-sm text-muted-foreground">
                {formatPercent(dashboard.yearly_summary?.acceptance_rate)} accepted
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm">Yearly Revenue</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">
                {formatCurrency(dashboard.yearly_summary?.accepted_value)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span className="text-sm">Monthly Hours</span>
              </div>
              <p className="text-2xl font-bold">{dashboard.monthly_summary?.monthly_hours || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">Hour Discrepancies</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">{dashboard.discrepancies_count || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="overview" className="flex-1 min-w-[100px]" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="profit" className="flex-1 min-w-[100px]" data-testid="tab-profit">Profit Margin</TabsTrigger>
          <TabsTrigger value="hours" className="flex-1 min-w-[100px]" data-testid="tab-hours">Employee Hours</TabsTrigger>
          <TabsTrigger value="discrepancies" className="flex-1 min-w-[100px]" data-testid="tab-discrepancies">Discrepancies</TabsTrigger>
          <TabsTrigger value="forecast" className="flex-1 min-w-[100px]" data-testid="tab-forecast">Forecast</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Year Selector */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Label>Year</Label>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchYearlyStats}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Yearly Stats */}
          {yearlyStats && (
            <>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quote Summary - {selectedYear}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span>Total Quotes</span>
                        <span className="font-bold">{yearlyStats.summary?.total_quotes}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-emerald-500" />
                          Accepted
                        </span>
                        <span className="font-bold text-emerald-600">
                          {yearlyStats.summary?.accepted_quotes} ({formatCurrency(yearlyStats.summary?.accepted_value)})
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="flex items-center gap-2">
                          <TrendingDown className="h-4 w-4 text-red-500" />
                          Unaccepted
                        </span>
                        <span className="font-bold text-red-600">
                          {yearlyStats.summary?.unaccepted_quotes} ({formatCurrency(yearlyStats.summary?.unaccepted_value)})
                        </span>
                      </div>
                      <div className="pt-2 border-t">
                        <div className="flex justify-between">
                          <span>Acceptance Rate</span>
                          <Badge className={yearlyStats.summary?.acceptance_rate >= 50 ? "bg-emerald-500" : "bg-orange-500"}>
                            {formatPercent(yearlyStats.summary?.acceptance_rate)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Pie Chart for Quote Status */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quote Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={[
                              { name: "Accepted", value: yearlyStats.summary?.accepted_quotes || 0, color: "#10b981" },
                              { name: "Unaccepted", value: yearlyStats.summary?.unaccepted_quotes || 0, color: "#ef4444" }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            <Cell fill="#10b981" />
                            <Cell fill="#ef4444" />
                          </Pie>
                          <Tooltip formatter={(value) => [value, "Quotes"]} />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Revenue Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Monthly Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={yearlyStats.monthly?.map(m => ({
                          month: new Date(2026, m.month - 1).toLocaleString("default", { month: "short" }),
                          accepted: m.accepted_value || 0,
                          total: m.total_value || 0,
                          quotes: m.total_quotes || 0
                        }))}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorAccepted" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" className="text-xs" />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} className="text-xs" />
                        <Tooltip 
                          formatter={(value, name) => [formatCurrency(value), name === "accepted" ? "Accepted Revenue" : "Total Value"]}
                          labelStyle={{ color: "hsl(var(--foreground))" }}
                          contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))" }}
                        />
                        <Legend />
                        <Area type="monotone" dataKey="total" stroke="#3b82f6" fillOpacity={1} fill="url(#colorTotal)" name="Total Value" />
                        <Area type="monotone" dataKey="accepted" stroke="#10b981" fillOpacity={1} fill="url(#colorAccepted)" name="Accepted Revenue" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Top Companies Bar Chart */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Top Companies by Revenue</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={yearlyStats.by_company?.slice(0, 5).map(c => ({
                            name: c.company_name?.substring(0, 15) + (c.company_name?.length > 15 ? "..." : ""),
                            revenue: c.accepted_value || 0,
                            quotes: c.accepted_quotes || 0
                          }))}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                          <YAxis type="category" dataKey="name" width={100} className="text-xs" />
                          <Tooltip 
                            formatter={(value) => [formatCurrency(value), "Revenue"]}
                            contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))" }}
                          />
                          <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Company Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {yearlyStats.by_company?.slice(0, 5).map((company, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{company.company_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {company.accepted_quotes}/{company.total_quotes} accepted
                            </p>
                          </div>
                          <span className="font-bold">{formatCurrency(company.accepted_value)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Monthly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Total Quotes</TableHead>
                        <TableHead className="text-right">Accepted</TableHead>
                        <TableHead className="text-right">Total Value</TableHead>
                        <TableHead className="text-right">Accepted Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {yearlyStats.monthly?.map((month) => (
                        <TableRow key={month.month}>
                          <TableCell>
                            {new Date(2026, month.month - 1).toLocaleString("default", { month: "long" })}
                          </TableCell>
                          <TableCell className="text-right">{month.total_quotes}</TableCell>
                          <TableCell className="text-right">{month.accepted_quotes}</TableCell>
                          <TableCell className="text-right">{formatCurrency(month.total_value)}</TableCell>
                          <TableCell className="text-right text-emerald-600">
                            {formatCurrency(month.accepted_value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Profit Margin Tab */}
        <TabsContent value="profit" className="space-y-6">
          {/* Date Range */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="space-y-1">
                  <Label>Start Date</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" />
                </div>
                <div className="space-y-1">
                  <Label>End Date</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" />
                </div>
                <Button className="mt-6" onClick={fetchProfitMargin}>
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate
                </Button>
                {isOfficeManager && (
                  <Button variant="outline" className="mt-6" onClick={() => setExpenditureDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Expenditure
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {profitMargin && (
            <>
              {/* Section A: Basic */}
              <Card>
                <CardHeader className="bg-blue-50 dark:bg-blue-950">
                  <CardTitle className="text-lg">Section A: Basic Profit</CardTitle>
                  <CardDescription>{profitMargin.section_a?.formula}</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Revenue</p>
                      <p className="text-xl font-bold">{formatCurrency(profitMargin.section_a?.revenue)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Employee Costs</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(profitMargin.section_a?.employee_costs)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Gross Profit</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(profitMargin.section_a?.gross_profit)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Margin</p>
                      <Badge className={profitMargin.section_a?.profit_margin_percent >= 30 ? "bg-emerald-500" : "bg-orange-500"}>
                        {formatPercent(profitMargin.section_a?.profit_margin_percent)}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section B: With Overhead */}
              <Card>
                <CardHeader className="bg-purple-50 dark:bg-purple-950">
                  <CardTitle className="text-lg">Section B: With Overhead & Expenditures</CardTitle>
                  <CardDescription>{profitMargin.section_b?.formula}</CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Revenue</p>
                      <p className="text-xl font-bold">{formatCurrency(profitMargin.section_b?.revenue)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Employee Costs</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(profitMargin.section_b?.employee_costs)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Overhead Costs</p>
                      <p className="text-xl font-bold text-orange-600">{formatCurrency(profitMargin.section_b?.overhead_costs)}</p>
                    </div>
                  </div>

                  {profitMargin.section_b?.overhead_breakdown?.length > 0 && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium mb-2">Overhead Breakdown</p>
                      {profitMargin.section_b.overhead_breakdown.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{item.name}</span>
                          <span>{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Expenditures</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(profitMargin.section_b?.expenditures_total)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Manual Additions</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(profitMargin.section_b?.manual_additions)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Net Profit</p>
                      <p className={`text-xl font-bold ${profitMargin.section_b?.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {formatCurrency(profitMargin.section_b?.net_profit)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <span>Profit Margin</span>
                    <Badge className={profitMargin.section_b?.profit_margin_percent >= 20 ? "bg-emerald-500" : "bg-orange-500"}>
                      {formatPercent(profitMargin.section_b?.profit_margin_percent)}
                    </Badge>
                  </div>

                  <p className="text-xs text-muted-foreground italic">
                    {profitMargin.section_b?.unaccounted_note}
                  </p>
                </CardContent>
              </Card>

              {/* Section C: Custom */}
              <Card>
                <CardHeader className="bg-green-50 dark:bg-green-950">
                  <CardTitle className="text-lg">Section C: Custom Calculations</CardTitle>
                  <CardDescription>Define custom formulas using available variables</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-medium mb-2">Available Variables</p>
                      <div className="space-y-1 text-sm">
                        {Object.entries(profitMargin.section_c?.available_variables || {}).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <code className="text-xs bg-muted px-1 rounded">{key}</code>
                            <span>{typeof value === "number" ? (key.includes("hours") ? value : formatCurrency(value)) : value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Custom Formulas</p>
                      {profitMargin.section_c?.formulas?.length > 0 ? (
                        <div className="space-y-2">
                          {profitMargin.section_c.formulas.map((f, idx) => (
                            <div key={idx} className="p-2 border rounded">
                              <p className="font-medium">{f.name}</p>
                              <code className="text-xs">{f.formula}</code>
                              {f.error ? (
                                <p className="text-red-500 text-sm">Error: {f.error}</p>
                              ) : (
                                <p className="text-emerald-600 font-bold">{formatCurrency(f.result)}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No custom formulas defined. Add them in Settings.</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Expenditures List */}
              {expenditures.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recorded Expenditures</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          {isOfficeManager && <TableHead className="w-16"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expenditures.slice(0, 10).map((exp) => (
                          <TableRow key={exp.id}>
                            <TableCell>{exp.date}</TableCell>
                            <TableCell>{exp.name}</TableCell>
                            <TableCell><Badge variant="outline">{exp.category}</Badge></TableCell>
                            <TableCell className="text-right">{formatCurrency(exp.amount)}</TableCell>
                            {isOfficeManager && (
                              <TableCell>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteExpenditure(exp.id)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Employee Hours Tab */}
        <TabsContent value="hours" className="space-y-6">
          {employeeHours && (
            <>
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Hours</p>
                    <p className="text-2xl font-bold">{employeeHours.totals?.total_hours || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Highrise Hours</p>
                    <p className="text-2xl font-bold text-blue-600">{employeeHours.totals?.highrise_hours || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50 dark:bg-green-950">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Lowrise Hours</p>
                    <p className="text-2xl font-bold text-green-600">{employeeHours.totals?.lowrise_hours || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Overtime Hours</p>
                    <p className="text-2xl font-bold text-orange-600">{employeeHours.totals?.overtime_hours || 0}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Hours by Employee</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Highrise</TableHead>
                        <TableHead className="text-right">Lowrise</TableHead>
                        <TableHead className="text-right">Overtime</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employeeHours.by_employee?.map((emp) => (
                        <TableRow key={emp.employee_id}>
                          <TableCell className="font-medium">{emp.employee_name}</TableCell>
                          <TableCell><Badge variant="outline">{emp.employee_type}</Badge></TableCell>
                          <TableCell className="text-right">{emp.total_hours}</TableCell>
                          <TableCell className="text-right text-blue-600">{emp.highrise_hours}</TableCell>
                          <TableCell className="text-right text-green-600">{emp.lowrise_hours}</TableCell>
                          <TableCell className="text-right text-orange-600">{emp.overtime_hours}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Discrepancies Tab */}
        <TabsContent value="discrepancies" className="space-y-6">
          {discrepancies && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Hour Discrepancy Summary - {selectedYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Completed Quotes</p>
                      <p className="text-xl font-bold">{discrepancies.summary?.total_completed_quotes}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">With Discrepancies</p>
                      <p className="text-xl font-bold text-orange-600">{discrepancies.summary?.quotes_with_discrepancies}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Quoted Hours</p>
                      <p className="text-xl font-bold">{discrepancies.summary?.total_quoted_hours}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Actual Hours</p>
                      <p className="text-xl font-bold">{discrepancies.summary?.total_actual_hours}</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <p className="text-sm">
                      Overall Difference: <span className="font-bold">{discrepancies.summary?.overall_difference} hours</span>
                      ({formatPercent(discrepancies.summary?.overall_difference_percent)} {discrepancies.summary?.overall_difference > 0 ? "over" : "under"})
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quotes with Hour Discrepancies</CardTitle>
                  <CardDescription>Showing quotes where actual hours differ by more than 10% from quoted</CardDescription>
                </CardHeader>
                <CardContent>
                  {discrepancies.discrepancies?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Quote #</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead className="text-right">Quoted</TableHead>
                          <TableHead className="text-right">Actual</TableHead>
                          <TableHead className="text-right">Diff</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discrepancies.discrepancies.map((d) => (
                          <TableRow key={d.quote_id}>
                            <TableCell className="font-medium">{d.quote_number}</TableCell>
                            <TableCell>{d.company_name}</TableCell>
                            <TableCell className="text-right">{d.quoted_hours}h</TableCell>
                            <TableCell className="text-right">{d.actual_hours}h</TableCell>
                            <TableCell className="text-right">
                              <Badge className={d.over_under === "over" ? "bg-red-500" : "bg-blue-500"}>
                                {d.over_under === "over" ? "+" : ""}{d.difference}h ({formatPercent(d.difference_percent)})
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{d.over_under}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No significant discrepancies found</p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="space-y-6">
          {seasonalForecast && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Seasonal Forecast - {seasonalForecast.current_year}</CardTitle>
                  <CardDescription>Based on historical data from previous periods</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {seasonalForecast.seasonal_periods?.map((period, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h4 className="font-semibold">{period.period_name}</h4>
                            <p className="text-sm text-muted-foreground">{period.start} to {period.end}</p>
                          </div>
                          {period.forecast?.expected_hours > 0 && (
                            <Badge className="bg-primary">
                              Expected: {period.forecast.expected_hours}h
                            </Badge>
                          )}
                        </div>

                        {period.forecast && (
                          <div className="grid md:grid-cols-4 gap-4 mb-4 p-3 bg-muted rounded">
                            <div>
                              <p className="text-xs text-muted-foreground">Expected Hours</p>
                              <p className="font-bold">{period.forecast.expected_hours}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Highrise</p>
                              <p className="font-bold text-blue-600">{period.forecast.expected_highrise_hours}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Lowrise</p>
                              <p className="font-bold text-green-600">{period.forecast.expected_lowrise_hours}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Expected Revenue</p>
                              <p className="font-bold text-emerald-600">{formatCurrency(period.forecast.expected_revenue)}</p>
                            </div>
                          </div>
                        )}

                        <div className="grid md:grid-cols-3 gap-2">
                          {period.historical_years?.map((year) => (
                            <div key={year.year} className="p-2 border rounded text-sm">
                              <p className="font-medium">{year.year}</p>
                              <p>Hours: {year.total_hours} | Revenue: {formatCurrency(year.total_revenue)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Profit Margin Settings</DialogTitle>
            <DialogDescription>Configure overhead categories, manual additions, and seasonal periods</DialogDescription>
          </DialogHeader>

          {profitSettings && (
            <div className="space-y-6">
              {/* Overhead Categories */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Overhead Categories (Section B)</Label>
                {profitSettings.overhead_categories?.map((cat, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border rounded">
                    <span className="flex-1">{cat.name}</span>
                    <span>${cat.amount} ({cat.category_type})</span>
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveOverhead(idx)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input placeholder="Name" value={newOverhead.name} onChange={(e) => setNewOverhead({...newOverhead, name: e.target.value})} />
                  <Input type="number" placeholder="Amount" value={newOverhead.amount} onChange={(e) => setNewOverhead({...newOverhead, amount: parseFloat(e.target.value) || 0})} className="w-24" />
                  <Select value={newOverhead.category_type} onValueChange={(v) => setNewOverhead({...newOverhead, category_type: v})}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="variable">Variable</SelectItem>
                      <SelectItem value="percentage">% of Revenue</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAddOverhead}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>

              {/* Manual Additions */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Manual Additions</Label>
                {profitSettings.manual_additions?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border rounded">
                    <span className="flex-1">{item.name}</span>
                    <span>${item.amount}</span>
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveManualAddition(idx)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input placeholder="Name" value={newManualAddition.name} onChange={(e) => setNewManualAddition({...newManualAddition, name: e.target.value})} />
                  <Input type="number" placeholder="Amount" value={newManualAddition.amount} onChange={(e) => setNewManualAddition({...newManualAddition, amount: parseFloat(e.target.value) || 0})} className="w-32" />
                  <Button onClick={handleAddManualAddition}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>

              {/* Seasonal Periods */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Seasonal Periods (Custom)</Label>
                {profitSettings.seasonal_periods?.map((period, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border rounded">
                    <span className="flex-1">{period.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {period.start_month}/{period.start_day} - {period.end_month}/{period.end_day}
                    </span>
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveSeasonalPeriod(idx)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <div className="grid grid-cols-6 gap-2">
                  <Input placeholder="Name" value={newSeasonalPeriod.name} onChange={(e) => setNewSeasonalPeriod({...newSeasonalPeriod, name: e.target.value})} className="col-span-2" />
                  <Input type="number" min="1" max="12" placeholder="Start M" value={newSeasonalPeriod.start_month} onChange={(e) => setNewSeasonalPeriod({...newSeasonalPeriod, start_month: parseInt(e.target.value) || 1})} />
                  <Input type="number" min="1" max="31" placeholder="Start D" value={newSeasonalPeriod.start_day} onChange={(e) => setNewSeasonalPeriod({...newSeasonalPeriod, start_day: parseInt(e.target.value) || 1})} />
                  <Input type="number" min="1" max="12" placeholder="End M" value={newSeasonalPeriod.end_month} onChange={(e) => setNewSeasonalPeriod({...newSeasonalPeriod, end_month: parseInt(e.target.value) || 12})} />
                  <Button onClick={handleAddSeasonalPeriod}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expenditure Dialog */}
      <Dialog open={expenditureDialogOpen} onOpenChange={setExpenditureDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Expenditure</DialogTitle>
            <DialogDescription>Record a business expenditure</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={newExpenditure.name} onChange={(e) => setNewExpenditure({...newExpenditure, name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newExpenditure.category} onValueChange={(v) => setNewExpenditure({...newExpenditure, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="fleet">Fleet</SelectItem>
                    <SelectItem value="supplies">Supplies</SelectItem>
                    <SelectItem value="labor">Labor</SelectItem>
                    <SelectItem value="overhead">Overhead</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" value={newExpenditure.amount} onChange={(e) => setNewExpenditure({...newExpenditure, amount: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={newExpenditure.date} onChange={(e) => setNewExpenditure({...newExpenditure, date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={newExpenditure.description} onChange={(e) => setNewExpenditure({...newExpenditure, description: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenditureDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateExpenditure} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Expenditure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
